import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FiSearch, FiX, FiChevronRight } from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';
import { useGlobalSearch } from '../../hooks/useGlobalSearch';
import { SearchResult } from '../../api/rpc/rpcSearch';
import { useClient } from '../../contexts/ClientContext';
import { useLang } from '../../contexts/LanguageContext';

interface GlobalSearchProps {
  company?: string;
}

const ENTITY_LABEL_KEYS: Record<string, string> = {
  clients: 'entityClients',
  leads: 'entityLeads',
  tasks: 'entityTasks',
  orders: 'entityOrders',
  inventory: 'entityInventory',
  users: 'entityUsers',
  proposals: 'entityProposals',
};

const ENTITY_ICONS: Record<string, string> = {
  clients: '👤',
  leads: '📋',
  tasks: '✅',
  orders: '📦',
  inventory: '📊',
  users: '👥',
  proposals: '📄',
};

/**
 * Get route for a search result
 */
function getRouteForResult(result: SearchResult, company: string, userRole?: string): string {
  const base = `/${company}`;
  
  switch (result.type) {
    case 'clients':
      // Correct route: /:company/client/:id (not /clients/:id)
      return `${base}/client/${result.id}`;
    case 'leads':
      // Leads page doesn't have a direct ID route, use query param
      return `${base}/leads?leadId=${result.id}`;
    case 'tasks':
      // Tasks route depends on role
      if (userRole === 'admin') {
        return `${base}/admin/tasks?taskId=${result.id}`;
      } else if (userRole === 'manager') {
        return `${base}/manager/tasks?taskId=${result.id}`;
      }
      return `${base}/admin/tasks?taskId=${result.id}`;
    case 'orders':
      return `${base}/admin/orders?orderId=${result.id}`;
    case 'inventory':
      return `${base}/admin/inventory?itemId=${result.id}`;
    case 'users':
      // Users can be agents or managers
      if (result.meta.role === 'agent') {
        return `${base}/agents/${result.id}`;
      } else if (result.meta.role === 'manager' || result.meta.role === 'admin') {
        // Check if there's a manager profile route
        return `${base}/admin/manager/${result.id}`;
      }
      return `${base}/agents/${result.id}`;
    case 'proposals':
      return `${base}/admin/proposals/${result.id}`;
    default:
      return base;
  }
}

/**
 * Debounce hook
 */
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

export default function GlobalSearch({ company: propCompany }: GlobalSearchProps) {
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const { company: companyFromParams } = useParams<{ company?: string }>();
  const { client } = useClient();
  
  const company = propCompany || companyFromParams || '';
  
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Debounce query (250ms)
  const debouncedQuery = useDebounce(query, 250);

  // Fetch search results (only if company is valid)
  const { data, isLoading, isFetching } = useGlobalSearch(
    debouncedQuery, 
    company && company !== 'superadmin' ? company : undefined
  );

  // Flatten all results for keyboard navigation
  const allResults = useMemo(() => {
    if (!data || typeof data !== 'object') return [];
    return (data as any).results || [];
  }, [data]);

  // Grouped results for display
  const groupedResults = useMemo(() => {
    if (!data || typeof data !== 'object') return {};
    return (data as any).grouped || {};
  }, [data]);

  // Check if query has at least 2 letters OR 2 numbers
  const hasMinimumInput = useMemo(() => {
    if (!debouncedQuery) return false;
    const letters = (debouncedQuery.match(/[א-תa-zA-Z]/g) || []).length;
    const numbers = (debouncedQuery.match(/\d/g) || []).length;
    return letters >= 2 || numbers >= 2;
  }, [debouncedQuery]);

  // Handle result selection (must be defined before useEffects that use it)
  const handleSelectResult = useCallback((result: SearchResult) => {
    if (!company || company === 'superadmin') return;
    
    const route = getRouteForResult(result, company, client?.role);
    
    // Close dropdown and clear input before navigation
    setIsOpen(false);
    setQuery('');
    inputRef.current?.blur();
    
    // Navigate to the result page
    navigate(route);
  }, [company, client?.role, navigate]);

  // Handle keyboard shortcut (Cmd/Ctrl + K)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Cmd+K (Mac) or Ctrl+K (Windows/Linux)
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setIsOpen(true);
        setTimeout(() => {
          inputRef.current?.focus();
        }, 0);
      }
      
      // Escape to close
      if (e.key === 'Escape' && isOpen) {
        setIsOpen(false);
        setQuery('');
        inputRef.current?.blur();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  // Handle keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex((prev) => (prev < allResults.length - 1 ? prev + 1 : prev));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex((prev) => (prev > 0 ? prev - 1 : 0));
      } else if (e.key === 'Enter' && allResults[selectedIndex]) {
        e.preventDefault();
        handleSelectResult(allResults[selectedIndex]);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, allResults, selectedIndex, handleSelectResult]);

  // Reset selected index when results change
  useEffect(() => {
    setSelectedIndex(0);
  }, [allResults.length]);

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
        setQuery('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // Scroll selected item into view
  useEffect(() => {
    if (!isOpen || selectedIndex < 0) return;
    
    const selectedElement = dropdownRef.current?.querySelector(
      `[data-result-index="${selectedIndex}"]`
    );
    if (selectedElement) {
      selectedElement.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, [selectedIndex, isOpen]);

  // Don't show search on superadmin routes or when no company
  // This must be AFTER all hooks
  if (!company || company === 'superadmin') {
    return null;
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setQuery(value);
    setIsOpen(true);
  };

  const handleInputFocus = () => {
    setIsOpen(true);
  };

  const handleClear = () => {
    setQuery('');
    setIsOpen(false);
    inputRef.current?.focus();
  };

  const hasResults = allResults.length > 0;
  const showLoading = isLoading || isFetching;
  const showEmpty = !showLoading && hasMinimumInput && !hasResults;

  return (
    <div ref={containerRef} className="relative flex-1 max-w-md mx-2 sm:mx-4">
      {/* Search Input */}
      <div className="relative">
        <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
          <FiSearch className="text-slate-500 text-base" />
        </div>
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          placeholder={t('common.searchAllPlaceholder')}
          dir={lang === 'en' ? 'ltr' : 'rtl'}
          className={`
            w-full pl-10 pr-10 py-2.5 text-sm
            border-2 border-slate-300 rounded-lg
            bg-white shadow-sm
            text-slate-900 placeholder-slate-500
            focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500
            hover:border-slate-400
            transition-all duration-200
            ${lang === 'en' ? 'text-left' : 'text-right'}
          `}
        />
        {query && (
          <button
            onClick={handleClear}
            className="absolute inset-y-0 left-0 flex items-center pl-3 text-slate-400 hover:text-slate-600 transition-colors"
            aria-label={t('common.clearSearch')}
          >
            <FiX className="text-sm" />
          </button>
        )}
        {showLoading && (
          <div className="absolute inset-y-0 left-0 flex items-center pl-3">
            <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
          </div>
        )}
      </div>

      {/* Dropdown Results */}
      <AnimatePresence>
        {isOpen && (hasResults || showEmpty || showLoading) && debouncedQuery && (
          <motion.div
            ref={dropdownRef}
            initial={{ opacity: 0, y: -8, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: 0.98 }}
            transition={{ duration: 0.15 }}
            className="
              absolute z-[200] mt-2 w-full
              bg-white rounded-xl border border-slate-200/80
              shadow-2xl max-h-[60vh] overflow-y-auto
            "
            style={{
              scrollbarWidth: 'thin',
              scrollbarColor: '#cbd5e1 transparent',
            }}
          >
            {showLoading && !hasMinimumInput ? (
              <div className="p-4 text-center text-slate-400 text-sm">
                {t('header.searchMinChars')}
              </div>
            ) : showLoading ? (
              <div className="p-4 text-center text-slate-400 text-sm">
                {t('header.searching')}
              </div>
            ) : showEmpty ? (
              <div className="p-6 text-center text-slate-400 text-sm">
                {t('header.noResultsFor', { query: debouncedQuery })}
              </div>
            ) : (
              <>
                {/* Grouped Results */}
                {Object.entries(groupedResults).map(([type, results]) => {
                  if (!Array.isArray(results) || results.length === 0) return null;
                  
                  return (
                    <div key={type} className="border-b border-slate-100 last:border-b-0">
                      <div className="px-3 py-2 bg-slate-50/50 border-b border-slate-100">
                        <div className="flex items-center gap-2 text-xs font-semibold text-slate-600">
                          <span>{ENTITY_ICONS[type] || '📄'}</span>
                          <span>{ENTITY_LABEL_KEYS[type] ? t(`header.${ENTITY_LABEL_KEYS[type]}`) : type}</span>
                          <span className="text-slate-400">({results.length})</span>
                        </div>
                      </div>
                      {results.map((result, idx) => {
                        const globalIndex = allResults.findIndex((r: SearchResult) => r.id === result.id && r.type === result.type);
                        const isSelected = globalIndex === selectedIndex;
                        
                        return (
                          <div
                            key={`${result.type}-${result.id}`}
                            data-result-index={globalIndex}
                            onClick={() => handleSelectResult(result)}
                            className={`
                              px-4 py-3 cursor-pointer transition-colors
                              ${isSelected ? 'bg-blue-50 border-r-2 border-blue-500' : 'hover:bg-slate-50'}
                            `}
                          >
                            <div className="flex items-start justify-between gap-3">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-1">
                                  <span className="text-xs font-medium text-slate-500 bg-slate-100 px-2 py-0.5 rounded">
                                    {ENTITY_LABEL_KEYS[result.type] ? t(`header.${ENTITY_LABEL_KEYS[result.type]}`) : result.type}
                                  </span>
                                </div>
                                <div className="text-sm font-medium text-slate-900 truncate">
                                  {result.title}
                                </div>
                                {result.subtitle && (
                                  <div className="text-xs text-slate-500 truncate mt-0.5">
                                    {result.subtitle}
                                  </div>
                                )}
                                {result.meta.phone && (
                                  <div className="text-xs text-slate-400 mt-1">
                                    📞 {result.meta.phone}
                                  </div>
                                )}
                                {result.meta.email && (
                                  <div className="text-xs text-slate-400 mt-0.5">
                                    ✉️ {result.meta.email}
                                  </div>
                                )}
                                {result.type === 'proposals' && result.meta.amount && (
                                  <div className="text-xs text-slate-400 mt-0.5">
                                    💰 {new Intl.NumberFormat('he-IL', {
                                      style: 'currency',
                                      currency: result.meta.currency || 'ILS'
                                    }).format(result.meta.amount)}
                                  </div>
                                )}
                              </div>
                              <FiChevronRight className="text-slate-400 flex-shrink-0 mt-1" />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
                
                {/* Footer */}
                {allResults.length > 0 && (
                  <div className="px-4 py-2 bg-slate-50/50 border-t border-slate-100 text-center">
                    <div className="text-xs text-slate-500">
                      {allResults.length} תוצאות • לחץ Enter לפתיחה
                    </div>
                  </div>
                )}
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

